/**
 * ReportArchiveView Component
 *
 * Main archive view with tabs - Unified style matching FournisseursView:
 * - Rapports: Report series using 3-level architecture
 * - Vehicules: Archived vehicle dossiers with TCO
 *
 * Critical Rule: Snapshots are NEVER listed globally.
 * They are accessed ONLY through their parent series.
 */

import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Archive, FileText, Car } from 'lucide-react';
import { reportAPI } from '../../api.js';
import { PageTemplate } from '../common/PageTemplate.jsx';
import ReportSeriesView from './series/ReportSeriesView.jsx';
import SnapshotHistoryView from './snapshots/SnapshotHistoryView.jsx';
import VehicleArchiveView from '../vehicles/VehicleArchiveView.jsx';

// Tab configuration — `label` = repli FR, l'affichage passe par t('reports.archive.tabs.<id>')
const ARCHIVE_TABS = [
  { id: 'reports', label: 'Rapports', icon: FileText },
  { id: 'vehicles', label: 'Véhicules', icon: Car },
];

function ReportArchiveView({ onNavigate }) {
  const { t } = useTranslation();
  const tabs = useMemo(() => ARCHIVE_TABS.map(tab => ({ ...tab, label: t(`reports.archive.tabs.${tab.id}`, { defaultValue: tab.label }) })), [t]);
  // Active tab
  const [activeTab, setActiveTab] = useState('reports');

  // View state for reports: 'series' (list) or 'history' (snapshots for a series)
  const [view, setView] = useState('series');
  const [selectedSeriesId, setSelectedSeriesId] = useState(null);

  // Handle navigation within archive
  const handleInternalNavigate = (viewName, params = {}) => {
    if (viewName === 'report-series-detail' && params.seriesId) {
      setSelectedSeriesId(params.seriesId);
      setView('history');
    } else if (viewName === 'series') {
      setView('series');
      setSelectedSeriesId(null);
    }
  };

  // Handle back from history to series list
  const handleBackToSeries = () => {
    setView('series');
    setSelectedSeriesId(null);
  };

  // Handle download from snapshot history
  const handleDownload = async (reportId, format = 'csv') => {
    try {
      await reportAPI.download(reportId, format);
    } catch (err) {
      console.error('Download error:', err);
      alert(t('reports.errors.download', { error: err.message }));
    }
  };

  // Render reports tab content
  const renderReportsContent = () => {
    if (view === 'history' && selectedSeriesId) {
      return (
        <SnapshotHistoryView
          seriesId={selectedSeriesId}
          onBack={handleBackToSeries}
          onDownload={handleDownload}
        />
      );
    }

    return (
      <ReportSeriesView
        onNavigate={handleInternalNavigate}
      />
    );
  };

  return (
    <PageTemplate
      icon={Archive}
      iconColor="text-slate-600"
      title={t('reports.archive.title')}
      subtitle={t('reports.archive.subtitle')}
      accentColor="slate"
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
    >
      {/* Tab Content */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {activeTab === 'reports' && renderReportsContent()}
        {activeTab === 'vehicles' && <VehicleArchiveView embedded />}
      </div>
    </PageTemplate>
  );
}

export default ReportArchiveView;
